import { useSelector } from "react-redux";
import { useProposalTypeData } from "../../../../../../hooks/proposal/use-proposal-type-data";
import ChannelTypeReview from "./channel-type-review";
import BasicInfoReview from "./basic-info-review";
import SelectedAttachableRiderTable from "./selected-attachable-rider-table";
import ClientInputForBorrowers from "../../second-step/age-borrowers/client-input-for-borrowers";
import ClientSupportingDocuments from "../../third-step/client-supporting-documents";
import Selection from "./selection";
import LevelRanking from "./level-ranking";
import UniformCoverage from "./uniform-coverage";
import SalaryRank from "./salary-rank";
import { selectCSVFile, selectExistingCsvFilePath, } from "../../../../../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice";
import { formatNumberWithComma, } from "../../../../../../utils/format-numbers.utils";
import ViewSection from "../../../../../ui/view-section";
import { useModalAccess } from "../../../../../global/modal/use-modal-access";

export default function ClientReviewProposal() {

    const {
        isPrototype,
        isCustomize,
        isAmountOfLoansSelected,
        isCoverageTypeSelected,
        hasSubTypeToG,
        hasOtherSpecifyGC,
        isShowPaymentTermAndLoanMaturity,
        isLevelRanking,
        isUniformCoverage,
        isSalaryRank,
        isLevelRankingAndSalaryRank,
        isProductGPA, 
        isProductGCLI, 
        isProductGYRT, 
        isProductGCI,
        basicInformation: selectedBasicInformation,
        affiliation: affiliations,
        natureOfBusinessItem,
        companyContactNumber,
        contactPersonNumber,
        groupClassification,
        othersGroupClassificationValue,
        typeOfGroup,
        othersTypeOfGroupValue,
        typeOfGroupSubType,
        ageProfile,
        modeOfPayment,
        proposalTypeState,
        prototype,
        product,
        basicPlan,
        coverage,
        amountOfLoans,
        minAmountOfLoans,
        maxAmountOfLoans,
        portfolioOfLoans,
        paymentTerm,
        maturityMonths,
        commission,
        services,
        notesCFE,
        channelType,
        showInputChannelType,
        channelTypeSubName,
        channelTypeNumber,
        channelTypeEmail,
        proposalAs,
        isForRenew,
        registeredNameForRenew,
        inputsByLevelRanking,
        inputsByUniformCoverage,
        inputsBySalaryRank,
        riders,
        ridersBMSR,
        ridersAmountOfLoans,
        ridersUniformCoverage,
        ridersLevelRanking,
        ridersSalaryRank,
        borrowerAgeOptionsList,
        minAgeProfile,
        maxAgeProfile,
        filesCFE,
    } = useProposalTypeData();
    const { isUpdateProposal } = useModalAccess() 

    const csvFile = useSelector(selectCSVFile);
    const existingCsvFilePath = useSelector(selectExistingCsvFilePath);
    const filePathName = existingCsvFilePath || "No file attached";
    const fileName = csvFile ? csvFile.name : isUpdateProposal ? filePathName : "No file attached";

    const ageProfileFormat = ageProfile.map((a) => a.age).join(" - ");

    const fAmountMin = formatNumberWithComma(minAmountOfLoans ?? "");
    const fAmountMax = formatNumberWithComma(maxAmountOfLoans ?? "");
    const formatMinMaxAmount = `${fAmountMin} - ${fAmountMax}`;
    const fAmountPortfolio = formatNumberWithComma(portfolioOfLoans ?? "");
    const maturityMonthsFormat = formatNumberWithComma(maturityMonths ?? "");
    const isPreview = true;

    return (
        <div className="w-full space-y-4">
            {/* ================================================= */}
            {/* FINAL REVIEW HEADER */}
            {/* ================================================= */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-1 px-4 py-4 sm:px-5">
                    <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#186cb2]/10 text-[#186cb2]">
                            <span className="text-xs font-bold">
                                4
                            </span>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-semibold text-gray-800 sm:text-base">
                                Final Review
                            </h2>
                            <p className="text-[11px] text-gray-500 sm:text-xs">
                                Review all information carefully before submitting your proposal.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {/* ================================================= */}
            {/* CHANNEL & PROPOSAL */}
            {/* ================================================= */}
            <ViewSection
                title="Channel & Proposal"
                description="Review the selected channel and proposal details."
            >
                <ChannelTypeReview
                    selectChannelType={channelType}
                    showInput={showInputChannelType}
                    selectProposalAs={proposalAs?.name}
                    sECRegisteredNameRenew={registeredNameForRenew}
                    isRenew={isForRenew}
                    channelSubName={channelTypeSubName}
                    channelSubNumber={channelTypeNumber}
                    channelSubEmail={channelTypeEmail}
                />
            </ViewSection>
            {/* ================================================= */}
            {/* BASIC INFORMATION */}
            {/* ================================================= */}
            <ViewSection
                title="Client Information"
                description="Review the client's company and contact information."
            >
                <BasicInfoReview
                    info={selectedBasicInformation}
                    affiliations={affiliations}
                    natureOfBusinessItem={natureOfBusinessItem?.name}
                    companyContactNumberName={companyContactNumber?.name}
                    companyContactNumberType={companyContactNumber?.id}
                    contactPersonContactName={contactPersonNumber?.name}
                    contactPersonContactType={contactPersonNumber?.id}
                />
            </ViewSection>
            {/* ================================================= */}
            {/* GROUP CLASSIFICATION */}
            {/* ================================================= */}
            <ViewSection
                title="Group Profile & Payment Details"
                description="Review the selected group classification, group type, age profile, and payment arrangement for this proposal."
            >
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">
                    <Selection
                        labelTitle="Group Classification"
                        labelName={groupClassification?.name ?? ""}
                        hasOtherSpecify={hasOtherSpecifyGC}
                        otherSpecifyValue={othersGroupClassificationValue ?? ""}
                    />
                    <Selection
                        labelTitle="Type of Group"
                        labelName={typeOfGroup?.name ?? ""}
                        hasOtherSpecify={!!othersTypeOfGroupValue}
                        otherSpecifyValue={othersTypeOfGroupValue ?? ""}
                        hasSubType={hasSubTypeToG}
                        sabTypeData={typeOfGroupSubType}
                    />
                    <Selection
                        labelTitle="Age Profile"
                        labelName={ageProfileFormat}
                    />
                    <Selection
                        labelTitle="Mode of Payment"
                        labelName={modeOfPayment?.name ?? ""}
                    />
                </div>
            </ViewSection>
            {/* ================================================= */}
            {/* PRODUCT & LOAN DETAILS */}
            {/* ================================================= */}
            <ViewSection
                title="Proposal Configuration & Loan Details"
                description="Review the proposal type, product and coverage configuration, loan details, payment terms, applicable charges, and required master list file."
            >
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {/* Proposal Type */}
                    <Selection
                        labelTitle="Type of Proposal"
                        labelName={proposalTypeState?.name ?? ""}
                    />
                    {/* Prototype */}
                    {isPrototype && (
                        <Selection
                            labelTitle="Type of Prototype"
                            labelName={prototype?.name ?? ""}
                        />
                    )}
                    {/* Customize */}
                    {isCustomize && (
                        <>
                            <Selection
                                labelTitle="Type of Product"
                                labelName={product?.name ?? ""}
                            />
                            <Selection
                                labelTitle="Basic Plans"
                                labelName={basicPlan?.name ?? ""}
                            />
                            {/* Coverage */}
                            {isCoverageTypeSelected && (
                                <Selection
                                    labelTitle="Coverage Type"
                                    labelName={coverage?.name ?? ""}
                                />
                            )}
                            {/* Amount of Loans */}
                            {isAmountOfLoansSelected && (
                                <>
                                    <Selection
                                        labelTitle="Amount of Loans"
                                        labelName={amountOfLoans?.name ?? ""}
                                    />
                                    <Selection
                                        labelTitle="Minimum & Maximum Loan Amount"
                                        labelName={formatMinMaxAmount}
                                    />
                                    <Selection
                                        labelTitle="Loan Portfolio"
                                        labelName={fAmountPortfolio}
                                    />
                                </>
                            )}
                            {/* Payment Term */}
                            {isShowPaymentTermAndLoanMaturity && (
                                <>
                                    <Selection
                                        labelTitle="Payment Term"
                                        labelName={paymentTerm?.name ?? ""}
                                    />
                                    <Selection
                                        labelTitle="Loan Maturity"
                                        labelName={`${maturityMonthsFormat} months`}
                                    />
                                </>
                            )}
                            {/* Commission */}
                            <Selection
                                labelTitle="Commission Rate"
                                labelName={`${commission ?? 0}%`}
                            />
                            {/* Service Fee */}
                            <Selection
                                labelTitle="Service/Collection Fee"
                                labelName={`${services ?? 0}%`}
                            />
                        </>
                    )}
                    {/* Master List */}
                    {!isProductGCLI && (
                        <Selection
                            labelTitle="Master List File"
                            labelName={fileName}
                        />
                    )}
                </div>
            </ViewSection>

            {/* ================================================= */}
            {/* COVERAGE DETAILS */}
            {/* ================================================= */}
            {isCustomize && isCoverageTypeSelected && (
                <ViewSection
                    title="Coverage Configuration"
                    description="Review how the coverage is structured and applied based on the selected coverage type."
                >
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                        {isLevelRanking && (
                            <LevelRanking
                                inputsByLevelRanking={inputsByLevelRanking}
                                selectedCoverageTypeState={coverage?.name}
                                isPreview={isPreview} 
                            />
                        )}
                        {isUniformCoverage && (
                            <UniformCoverage 
                                inputsByUniformCoverage={inputsByUniformCoverage}
                                selectedCoverageTypeState={coverage?.name}
                                isPreview={isPreview} 
                            />
                        )}
                        {isSalaryRank && (
                            <SalaryRank 
                                inputsBySalaryRank={inputsBySalaryRank}
                                selectedCoverageTypeState={coverage?.name}
                                isPreview={isPreview} 
                            />
                        )}
                    </div>
                </ViewSection>
            )}
            {/* ================================================= */}
            {/* ATTACHABLE RIDERS */}
            {/* ================================================= */}
            {isCustomize && !isProductGCI && (
                <ViewSection
                    title="Attachable Riders"
                    description="Review all selected riders and their corresponding coverage."
                >
                    <div className="overflow-x-auto">
                        <SelectedAttachableRiderTable 
                            isProductGCLI={isProductGCLI}
                            isSalaryRank={isSalaryRank}
                            isUniformCoverage={isUniformCoverage}
                            isLevelRankingAndSalaryRank={isLevelRankingAndSalaryRank}
                            selectedRiders={riders}
                            inputsBySalaryRank={inputsBySalaryRank}
                            inputsByLevelRanking={inputsByLevelRanking}
                            selectedCoverageTypeState={coverage}
                            selectedAmountOfLoansCheckBox={amountOfLoans}
                            inputsByUniformCoverage={inputsByUniformCoverage}
                            selectedBMSR={ridersBMSR}
                            isAmountOfLoan={isProductGCLI}
                            amountOfLoansRiders={ridersAmountOfLoans}
                            selectUniformCoverageRidersState={ridersUniformCoverage}
                            selectLevelRankingRidersState={ridersLevelRanking}
                            selectSalaryRankRidersState={ridersSalaryRank}
                            isLevelRanking={isLevelRanking}
                            isPreview={isPreview}
                        />
                    </div>
                </ViewSection>
            )}
            {/* ================================================= */}
            {/* BORROWERS + NOTES */}
            {/* ================================================= */}
            {isCustomize && (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {/* Borrowers */}
                    <ViewSection
                        title="Eligibility Age and Benefit Amount"
                        description="Review the borrower information and configuration for this proposal."
                    >
                        <ClientInputForBorrowers
                            isProductGPA={isProductGPA}
                            isProductGCLI={isProductGCLI}
                            isProductGYRT={isProductGYRT}
                            isProductGCI={isProductGCI}
                            borrowerAgeOptionsList={borrowerAgeOptionsList}
                            minAge={minAgeProfile}
                            maxAge={maxAgeProfile}
                            isPreview={isPreview}
                        />
                    </ViewSection>
                    {/* Notes */}
                    <ViewSection
                        title="Notes & Remarks"
                        description="Review any additional remarks or notes included with this proposal."
                    >
                        <div className="min-h-[180px] rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="rich-text-viewer">
                                {notesCFE ? (
                                    <div
                                        // className="ProseMirror break-words text-xs leading-5 text-gray-700"
                                        className="text-[13px]"
                                        dangerouslySetInnerHTML={{
                                            __html: notesCFE,
                                        }}
                                    />
                                ) : (
                                    <p className="text-xs italic text-gray-400">
                                        No notes added.
                                    </p>
                                )}
                            </div>
                        </div>
                    </ViewSection>
                </div>
            )}
            {/* ================================================= */}
            {/* SUPPORTING DOCUMENTS */}
            {/* ================================================= */}
            {isCustomize && (
                <ViewSection
                    title="Supporting Documents"
                    description="Review the documents attached to this proposal."
                >
                    <ClientSupportingDocuments
                        files={filesCFE}
                        isPreview={isPreview}
                    />
                </ViewSection>
            )}
        </div>
    );
}